import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Separator } from './ui/separator';
import { ClipboardList, Download } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const BulkSummaryCard = ({ materialTotals, grandTotal }) => {
  const exportToCSV = () => {
    let csv = 'Material,Category,Volume (cy),Weight (tons),Weight (lbs)\n';
    
    materialTotals.forEach(material => {
      const totalPounds = material.totalTons * 2000;
      csv += `${material.name},${material.category},${material.totalCubicYards.toFixed(2)},${material.totalTons.toFixed(2)},${totalPounds.toFixed(0)}\n`;
    });
    
    const grandTotalPounds = grandTotal.totalTons * 2000;
    csv += `\nGrand Total,,${grandTotal.totalCubicYards.toFixed(2)},${grandTotal.totalTons.toFixed(2)},${grandTotalPounds.toFixed(0)}`;
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bulk-material-calculation-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('Report exported successfully!');
  };

  return (
    <div className="space-y-4">
      <Card className="border border-border shadow-card bg-card sticky top-4">
        <CardHeader className="pb-3">
          <CardTitle className="text-xl flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-primary" />
            Summary by Material
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {materialTotals.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              No materials added yet
            </p>
          ) : (
            <>
              <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                {materialTotals.map((material, index) => (
                  <div key={index} className="space-y-2">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-semibold text-foreground text-sm">{material.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {material.count} {material.count === 1 ? 'entry' : 'entries'}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="bg-muted rounded p-2">
                        <p className="text-muted-foreground mb-1">Volume</p>
                        <p className="font-semibold text-foreground">
                          {material.totalCubicYards.toLocaleString(undefined, { maximumFractionDigits: 2 })} cy
                        </p>
                      </div>
                      <div className="bg-muted rounded p-2">
                        <p className="text-muted-foreground mb-1">Weight</p>
                        <p className="font-semibold text-primary">
                          {material.totalTons.toLocaleString(undefined, { maximumFractionDigits: 2 })} tons
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {(material.totalTons * 2000).toLocaleString(undefined, { maximumFractionDigits: 0 })} lbs
                        </p>
                      </div>
                    </div>
                    {index < materialTotals.length - 1 && <Separator className="mt-3" />}
                  </div>
                ))}
              </div>
              
              <Separator className="my-4" />
              
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 space-y-2">
                <h4 className="font-semibold text-foreground text-sm mb-3">Grand Total</h4>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Total Volume:</span>
                    <span className="text-base font-bold text-foreground">
                      {grandTotal.totalCubicYards.toLocaleString(undefined, { maximumFractionDigits: 2 })} cy
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Total Weight:</span>
                    <span className="text-lg font-bold text-primary">
                      {grandTotal.totalTons.toLocaleString(undefined, { maximumFractionDigits: 2 })} tons
                    </span>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-primary/20">
                    <span className="text-sm text-muted-foreground">Total Pounds:</span>
                    <span className="text-base font-bold text-primary">
                      {(grandTotal.totalTons * 2000).toLocaleString(undefined, { maximumFractionDigits: 0 })} lbs
                    </span>
                  </div>
                </div>
              </div>
              
              <Button
                onClick={exportToCSV}
                variant="outline"
                className="w-full mt-4 border-primary/50 text-primary hover:bg-primary/10"
              >
                <Download className="w-4 h-4 mr-2" />
                Export Report
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BulkSummaryCard;